import { formatArtists, type HomeRowDTO, pickI18n, type SongDTO } from '@akpl/shared';
import {
  Card,
  type CardBadge,
  cardRowHeight,
  colorFor,
  HeroCarousel,
  type HeroItem,
  Row,
} from '@akpl/ui-tv';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import { useRouter } from '../router';
import { useSongActions } from '../song/songActions';
import { useT } from '../useT';
import { playlistTarget } from './Playlist';
import styles from './screens.module.css';
import type { ScreenProps } from './types';
import { useScreenFocus } from './useScreenFocus';

export const HOME_FOCUS = 'hero-play';

/**
 * Row heights come from the card contract (two title lines + the "N canciones"/artists line,
 * `cardRowHeight`), never from a hand-tuned number: the old 330 px assumed a one-line
 * playlist name, and a name that wrapped put its count line under the next row.
 */
const PLAYLIST_ROW_H = cardRowHeight('playlist');
const SONG_ROW_H = cardRowHeight('song');

/**
 * Phase 1 shows "Nuevo" and nothing else: no VIP badge, no lock (docs/04 §7.7). The gating
 * itself is untouched — a locked song still answers with the upsell toast.
 */
function songBadge(song: SongDTO, recentLabel: string): CardBadge | undefined {
  return song.tier === 'recent' ? { label: recentLabel, tone: 'new' } : undefined;
}

/**
 * Home (Figma TV-02). One `GET /home` call, one renderer per row type (docs/04 §3):
 * `heroes` → hero carousel with `poster_tv` and 8 s auto-advance, `playlists` → playlist
 * cards, `songs` → song cards with the "Nuevo" badge. Enter on a song always opens the song
 * action dialog (docs/04 §7.5); playlists and playlist heroes open the playlist view.
 */
export default function Home(_props: ScreenProps<'home'>) {
  const { t, i18n } = useT();
  const locale = i18n.language === 'en' ? 'en' : 'es';
  const navigate = useRouter((s) => s.navigate);
  const { openSongActions, overlay } = useSongActions();

  const home = useQuery({ queryKey: ['home'], queryFn: () => api.home() });
  const rows: HomeRowDTO[] = useMemo(() => home.data?.rows ?? [], [home.data]);
  useScreenFocus('home', HOME_FOCUS, rows.length > 0);

  // Focus-driven vertical scrolling: translate the column so the active block is at the top.
  const blocks = useRef<(HTMLDivElement | null)[]>([]);
  const [offset, setOffset] = useState(0);
  const activate = useCallback((index: number) => {
    const el = blocks.current[index];
    setOffset(el ? el.offsetTop : 0);
  }, []);

  const heroRow = rows.find((row) => row.type === 'heroes');
  const heroItems = useMemo<HeroItem[]>(() => {
    if (!heroRow || heroRow.type !== 'heroes') return [];
    return heroRow.items.map((hero) => {
      const isPlaylist = hero.type === 'playlist';
      // Figma TV-02: "24 canciones · Queen, Soda Stereo, Bon Jovi" — description = the preview.
      const subtitle = isPlaylist
        ? [t('tv:playlist.songs', { count: hero.playlist.total }), hero.playlist.description]
            .filter(Boolean)
            .join(' · ')
        : formatArtists(hero.song.artists, locale);
      return {
        id: hero.id,
        kicker: isPlaylist ? t('tv:home.featuredPlaylist') : t('tv:home.featuredSong'),
        title: isPlaylist ? pickI18n(hero.playlist.name, locale) : hero.song.title,
        subtitle,
        imageUrl: hero.posterTvUrl || null,
        color: colorFor(hero.refId),
        actions: isPlaylist
          ? [
              { key: 'play', label: t('common:actions.play'), primary: true },
              { key: 'open', label: t('tv:home.viewList') },
            ]
          : [{ key: 'play', label: t('common:actions.play'), primary: true }],
      };
    });
  }, [heroRow, locale, t]);

  // Hero(song) behaves exactly like a song card; both hero(playlist) actions open the list —
  // the TV never plays a whole list blind (docs/04 §3).
  const onHeroAction = (item: HeroItem) => {
    const hero =
      heroRow?.type === 'heroes' ? heroRow.items.find((entry) => entry.id === item.id) : undefined;
    if (!hero) return;
    if (hero.type === 'song') openSongActions(hero.song);
    else navigate('playlist', playlistTarget(hero.playlist));
  };

  const recentLabel = t('common:tier.recent');

  return (
    <>
      <div className={styles.content} data-testid="screen-home">
        {home.isError ? (
          <div className={styles.empty} role="alert">
            {t('tv:home.loadError')}
          </div>
        ) : home.isPending ? (
          // UX-110: paired → Home showed a bare "Cargando…" for 3.5–13 s. The skeleton says
          // what is coming and that something is happening, in the shape the content will take.
          <div className={styles.skeleton} data-testid="home-skeleton" aria-busy="true">
            <div className={styles.skelLabel}>{t('tv:home.preparing')}</div>
            <div className={styles.skelBlock} />
            {[0, 1].map((row) => (
              <div key={row}>
                <div className={styles.skelRowTitle} />
                <div className={styles.skelRow}>
                  {[0, 1, 2, 3, 4].map((card) => (
                    <div key={card} className={styles.skelCard} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.empty}>{t('tv:home.empty')}</div>
        ) : (
          <div className={styles.scroller} style={{ transform: `translateY(${-offset}px)` }}>
            {rows.map((row, idx) => {
              const setRef = (el: HTMLDivElement | null) => {
                blocks.current[idx] = el;
              };
              if (row.type === 'heroes') {
                return (
                  <div
                    key={row.id}
                    ref={setRef}
                    className={styles.homeBlock}
                    data-testid={`home-block-${row.type}`}
                  >
                    <HeroCarousel
                      items={heroItems}
                      focusKey="hero"
                      onAction={onHeroAction}
                      onActive={() => activate(idx)}
                    />
                  </div>
                );
              }
              if (row.type === 'playlists') {
                return (
                  <div key={row.id} ref={setRef} data-testid={`home-block-${row.type}`}>
                    <Row
                      title={pickI18n(row.title, locale)}
                      count={row.items.length}
                      focusKey={`row-${row.id}`}
                      itemHeight={PLAYLIST_ROW_H}
                      onActive={() => activate(idx)}
                      renderItem={({ index, focusKey, onFocus }) => {
                        const playlist = row.items[index]!;
                        return (
                          <Card
                            variant="playlist"
                            title={pickI18n(playlist.name, locale)}
                            subtitle={t('tv:playlist.songs', { count: playlist.total })}
                            imageUrl={playlist.imageUrl}
                            color={colorFor(playlist.id)}
                            focusKey={focusKey}
                            onFocus={onFocus}
                            onSelect={() => navigate('playlist', playlistTarget(playlist))}
                          />
                        );
                      }}
                    />
                  </div>
                );
              }
              return (
                <div key={row.id} ref={setRef} data-testid={`home-block-${row.type}`}>
                  <Row
                    title={pickI18n(row.title, locale)}
                    count={row.items.length}
                    focusKey={`row-${row.id}`}
                    itemHeight={SONG_ROW_H}
                    onActive={() => activate(idx)}
                    renderItem={({ index, focusKey, onFocus }) => {
                      const song = row.items[index]!;
                      return (
                        <Card
                          variant="song"
                          title={song.title}
                          subtitle={formatArtists(song.artists, locale)}
                          imageUrl={song.imageUrl}
                          color={colorFor(song.id)}
                          badge={songBadge(song, recentLabel)}
                          focusKey={focusKey}
                          onFocus={onFocus}
                          onSelect={() => openSongActions(song)}
                        />
                      );
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      {overlay}
    </>
  );
}
